import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksApi } from '../api';
import { calendarApi } from '../../calendar/api';
import { useSocket } from '../../../shared/socket/useSocket';

// Fase 5.17 (05-remediation.md) -- `ComponentTaskHoy.jsx` (presentacional) hacía su propio
// `useSocket` + `useQueryClient` para el sync de CRUD de tareas (invalidation-first) y para la
// cache de eventos de calendario de la vista. Encapsulado acá.
export function useTasksSocketSync() {
    const qc = useQueryClient();
    useSocket(useMemo(() => {
        const invalidate = (payload) => {
            qc.invalidateQueries({ queryKey: ['tasks', 'list'] });
            qc.invalidateQueries({ queryKey: ['tasks', 'standalone'] });
            qc.invalidateQueries({ queryKey: ['whatsapp_task'] });

            // Actualizar la caché de whatsapp_task directamente si hay payload
            if (payload && (payload.id || payload.original_id)) {
                const taskId = String(payload.original_id || payload.id);
                qc.setQueryData(['whatsapp_task', taskId], (old) => {
                    if (!old) return old;
                    return { ...old, ...payload };
                });
            }
        };
        return { task_updated: invalidate, task_created: invalidate, task_deleted: invalidate };
    }, [qc]));
}

export function useTasksCalendarEventsCache() {
    const qc = useQueryClient();
    const patchEvent = useCallback((eventId, patch) => {
        qc.setQueriesData({ queryKey: ['tasks', 'calendar-events'] }, (d) => {
            if (!d?.calendarEvents) return d;
            return { ...d, calendarEvents: d.calendarEvents.map((e) => (String(e.id) === String(eventId) ? { ...e, ...patch } : e)) };
        });
    }, [qc]);
    const invalidateEvents = useCallback(
        () => qc.invalidateQueries({ queryKey: ['tasks', 'calendar-events'] }),
        [qc],
    );
    return { patchEvent, invalidateEvents };
}

export function useTaskSpaceName(spaceId) {
    return useQuery({
        queryKey: ['tasks', 'space-name', spaceId],
        queryFn: () => tasksApi.fetchSpaceName(spaceId),
        enabled: !!spaceId,
        staleTime: 5 * 60 * 1000,
    });
}

export function useUserSpaces(userDbId) {
    return useQuery({
        queryKey: ['tasks', 'user-spaces', userDbId],
        queryFn: async () => {
            const data = await tasksApi.fetchUserSpaces(userDbId);
            return data.success ? (data.spaces || []) : [];
        },
        enabled: !!userDbId,
    });
}

export function useCreateSpace(userDbId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ name, userId }) => tasksApi.createSpace(name, userId),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', 'user-spaces', userDbId] }),
    });
}

export function useHitos(spaceId) {
    return useQuery({
        queryKey: ['tasks', 'hitos', spaceId],
        queryFn: async () => {
            const data = await tasksApi.fetchHitos(spaceId);
            return data.success ? (data.hitos || []) : [];
        },
    });
}

export function useCreateHito(spaceId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => tasksApi.createHito(payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', 'hitos', spaceId] }),
    });
}

// `columnWidths` es la única pieza de estado de `ComponentTaskHoy.jsx` sin
// otro escritor (sockets/drag no la tocan) — semilla real de `useQuery`, sincronizada hacia el
// `useState` local vía un `useEffect` corto (mismo patrón que `useTableConfig` en la parte 2).
export function useColumnWidths(userId) {
    return useQuery({
        queryKey: ['tasks', 'column-widths', userId],
        queryFn: async () => {
            const data = await tasksApi.fetchColumnWidths(userId);
            return data.success && data.value ? data.value : null;
        },
        enabled: !!userId,
    });
}

export function useSaveColumnWidths(userId) {
    return useMutation({
        mutationFn: (value) => tasksApi.saveColumnWidths(userId, value),
    });
}

// slice 02.1 · paso a2 — `tasks`/`calendarEvents`/`reminderEvents` ya NO son `useState` en el
// componente: se leen directo de `useQuery`. `useUpdateTask` hace la escritura optimista sobre
// la cache (`onMutate` → todas las variantes de `['tasks','list']` + `['tasks','standalone']`),
// rollback en `onError`, y reconcilia con el servidor vía `invalidateQueries` en `onSettled`
// (+ el socket `task_updated` que el backend emite). Convención 0.8e: invalidation-first para
// CRUD, nunca `setQueryData` desde el payload de respuesta.

const applyTaskPatch = (id, fields) => (arr) =>
    Array.isArray(arr) ? arr.map((t) => (String(t.id) === String(id) ? { ...t, ...fields } : t)) : arr;

function invalidateTaskLists(qc) {
    qc.invalidateQueries({ queryKey: ['tasks', 'list'] });
    qc.invalidateQueries({ queryKey: ['tasks', 'standalone'] });
    qc.invalidateQueries({ queryKey: ['whatsapp_task'] });
}

export function useUpdateTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => tasksApi.updateTask(id, patch),
        onMutate: async ({ id, patch }) => {
            await qc.cancelQueries({ queryKey: ['tasks'] });
            await qc.cancelQueries({ queryKey: ['whatsapp_task'] });
            const prev = [
                ...qc.getQueriesData({ queryKey: ['tasks', 'list'] }),
                ...qc.getQueriesData({ queryKey: ['tasks', 'standalone'] }),
                ...qc.getQueriesData({ queryKey: ['whatsapp_task'] }),
            ];
            // `actorId`/`autoSchedule`/`duration_minutes` son campos de control del PUT, no
            // columnas — no se escriben en el objeto optimista.
            const fields = { ...patch };
            delete fields.actorId;
            delete fields.autoSchedule;
            delete fields.duration_minutes;
            const apply = applyTaskPatch(id, fields);
            qc.setQueriesData({ queryKey: ['tasks', 'list'] }, apply);
            qc.setQueriesData({ queryKey: ['tasks', 'standalone'] }, apply);
            
            // Optimistic update for whatsapp_task
            qc.setQueryData(['whatsapp_task', String(id)], (old) => {
                if (!old) return old;
                return { ...old, ...fields };
            });
            
            return { prev };
        },
        onError: (_err, _vars, ctx) => {
            ctx?.prev?.forEach(([key, data]) => qc.setQueryData(key, data));
        },
        onSettled: () => invalidateTaskLists(qc),
    });
}

export function useCreateTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => tasksApi.createTask(payload),
        // No se puede escribir optimista (aún no hay id); el socket `task_created` y este
        // invalidate cubren la aparición de la tarea nueva en la lista.
        onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', 'list'] }),
    });
}

export function useDeleteTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => tasksApi.deleteTask(id),
        onSuccess: () => invalidateTaskLists(qc),
    });
}

// --- slice 02.1 · paso a1: la lista de tareas / la tarea standalone / los eventos de
// calendario de la vista de tareas pasan a `useQuery` como *semilla*. `ComponentTaskHoy`
// mantiene su `useState` local (`tasks`/`calendarEvents`/`reminderEvents`, 30+ escritores
// no-fetch) sincronizado con un `useEffect` corto — mismo patrón que `useColumnWidths`.
// El paso a2 quita el espejo y convierte los ~27 escritores. Ver `02.1-tasks.md` §6.
// `['tasks','list']` y `['tasks','standalone']` llevan `staleTime: Infinity` +
// `refetchOnWindowFocus: false` (convención 0.8e — un socket las mantiene vivas, un refetch
// de fondo no debe pisar el estado optimista/de drag).

function buildTaskListParams({ activeSpaceId, timeRange, currentRange, dateOffset }) {
    const p = new URLSearchParams();
    if (activeSpaceId) p.set('space_id', activeSpaceId);
    if (timeRange === 'custom') {
        // "custom" colapsa a un rango degenerado (no hay selector real todavía); se pide el
        // dataset completo de forma explícita en vez de que "sin rango" sea "todo" implícito.
        p.set('all', 'true');
    } else {
        p.set('start', currentRange.startStr);
        p.set('end', currentRange.endStr);
        if (timeRange === 'hoy' && dateOffset === 0) p.set('includeUndated', 'true');
    }
    return p.toString();
}

export function useTasksList({ activeSpaceId, timeRange, currentRange, dateOffset, enabled = true }) {
    return useQuery({
        queryKey: ['tasks', 'list', {
            spaceId: activeSpaceId || null,
            startStr: currentRange.startStr,
            endStr: currentRange.endStr,
            timeRange,
            dateOffset,
        }],
        queryFn: async () => {
            const data = await tasksApi.fetchTasks(buildTaskListParams({ activeSpaceId, timeRange, currentRange, dateOffset }));
            return data.success ? (data.tasks || []) : [];
        },
        enabled,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
}

export function useStandaloneTask(standaloneTaskId) {
    return useQuery({
        queryKey: ['tasks', 'standalone', standaloneTaskId],
        queryFn: async () => {
            // `fetchTask` puede lanzar `ApiError` 403 `code:3005` (no-miembro del espacio, fix
            // de `task-share-link.md`) — se propaga como `query.error` y el componente pinta
            // la pantalla de no-miembro.
            const data = await tasksApi.fetchTask(standaloneTaskId);
            if (!data.success || !data.task) return [];
            const loaded = [data.task];
            try {
                // `parent_task_id` reemplaza el viejo `data.task.subtasks[]` embebido.
                const st = await tasksApi.fetchTasks(`parent_task_id=${standaloneTaskId}`);
                if (st.success && Array.isArray(st.tasks)) loaded.push(...st.tasks);
            } catch { /* las subtareas son opcionales */ }
            return loaded;
        },
        enabled: !!standaloneTaskId,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false, // no reintentar un 403
    });
}

export function useTasksCalendarEvents({ activeSpaceId, currentRange, onlyMyEvents, userId, userNick, enabled = true }) {
    return useQuery({
        queryKey: ['tasks', 'calendar-events', {
            spaceId: activeSpaceId || null,
            startStr: currentRange.startStr,
            endStr: currentRange.endStr,
            onlyMyEvents: !!onlyMyEvents,
            userId: userId || null,
        }],
        queryFn: async () => {
            const evData = await calendarApi.fetchEvents({
                start: `${currentRange.startStr}T00:00:00`,
                end: `${currentRange.endStr}T23:59:59`,
                onlyMine: onlyMyEvents,
                userId,
                userNick,
            });
            if (!evData.success) return { calendarEvents: [], reminderEvents: [] };
            const allEventsFetched = evData.events || [];
            const allEvents = activeSpaceId
                ? allEventsFetched.filter(e => !e.task_space_id || String(e.task_space_id) === String(activeSpaceId))
                : allEventsFetched;
            return {
                // `calendarEvents` se guarda con las fechas ya parseadas; `reminderEvents` no
                // (se preserva el comportamiento exacto del efecto original).
                calendarEvents: allEvents.map(e => ({ ...e, start: new Date(e.start_time), end: new Date(e.end_time) })),
                reminderEvents: allEvents.filter(e => e.type === 'recordatorio'),
            };
        },
        enabled,
    });
}

